import {
   BrickBox,
   BrickButton,
   BrickImage,
   BrickMailDefinition,
   BrickMailInstance,
   BrickProducts, BrickShipping, BrickStatus,
   BrickText
} from "./BrickMailType";

export const ImageBrickHtml = (brick: BrickImage) => `<!-- Image section -->
  <table class="module" role="module" data-type="text" border="0"
  cellpadding="0" cellspacing="0" width="100%"
  style="table-layout: fixed;"
  data-muid="580f22fc-39aa-468e-9753-3a0d0a23fd57"
  data-mc-module-version="2019-10-22">
  <tbody>
  <tr>
  <td style="padding:18px 0px 18px 0px; line-height:22px; text-align:inherit;"
  height="100%" valign="top"
  role="module-content">
  <div>
  <div style="font-family: inherit; font-size: 16px; text-align: ${brick.align || 'center'}; letter-spacing: 0.35rem">
  <span style="color: #000000; text-decoration: none">
  <img src="${brick.url}" title="${brick.title}" alt="${brick.title}"/>
  </span>
  </div>
  </div>
  </td>
  </tr>
  </tbody>
  </table>`

export const TextBrickHtml = (brick: BrickText, mail: BrickMailDefinition) => `<!-- Text section -->
  <table style="table-layout:fixed" class="module" role="module" data-type="text" border="0" cellpadding="0" cellspacing="0" width="100%">
  <tbody><tr><td style="line-height:130%;padding:${brick.margin || brick.padding || '0px 5px 0px 5px'}; text-align:${brick.align || 'center'};">
  <div style="line-height:130%;font-family: ${brick.fontFamily || mail.body?.font || 'Assistant, verdana, geneva, sans-serif'}; text-align: ${brick.align || 'center'};">
  <span style="line-height:130%;background-color: ${brick.backgroundColor || mail.body?.textBackgroundColor || 'inherit'}; color: ${brick.color || '#000000'}; font-family: ${brick.fontFamily || mail.body?.font || 'Assistant, verdana, geneva, sans-serif'}; font-size: ${brick.fontSize || mail.body?.fontSize || '14px'} !important; ${brick.bold || brick.fontWeight ? 'font-weight: ' + (brick.fontWeight || 'bold') + ';' : ''}">
  ${brick.value}
  </span>
  </div>
  </td></tr>
  </tbody></table>${!brick.disablePadding && mail.body?.spaceBetween ? `
  <table cellpadding="0" cellspacing="0" border="0" width="100">
  <tr><td width="100" height="${mail.body.spaceBetween || '15'}">&nbsp;</td></tr></table>
  ` : ``}
`

// Helper function to parse multi-language default values
const parseMultiLanguageDefaults = (
   args: (string | undefined)[],
   locale: string = 'en'
): { value: string | undefined; selectedDefault: string } => {
   const defaultValues: { [lang: string]: string } = {};
   let value: string | undefined;

   for (const arg of args) {
      if (typeof arg === 'string') {
         if (arg.startsWith("default=")) {
            // Parse single default
            const defaultValue = arg.split("=").slice(1).join("=");
            defaultValues["default"] = defaultValue;
         } else if (arg.startsWith("defaults=")) {
            // Parse multiple language defaults as JSON string
            try {
               const jsonStr = arg.substring(9);
               Object.assign(defaultValues, JSON.parse(jsonStr));
            } catch (e) {
               console.error('Failed to parse defaults JSON', e);
            }
         } else if (arg.startsWith("default.")) {
            // Parse individual language defaults like "default.en=Hello" or "default.fr=Bonjour"
            const [langKey, defaultValue] = arg.split("=");
            const lang = langKey.split(".")[1];
            defaultValues[lang] = defaultValue;
         } else {
            value = arg;
         }
      }
   }

   // Select appropriate default based on locale
   const selectedDefault = defaultValues[locale] || defaultValues['default'] || '';

   if (!value && selectedDefault) {
      value = selectedDefault;
   }

   return { value, selectedDefault };
};

// TagBuilder: turns { 'font-size': '14px', color: '#333' } → 'font-size:14px;color:#333;'
const TagBuilder = (
   tag: string,
   content: string,
   props: Record<string, string | undefined>
): string => {
   const style = Object.entries(props)
      .filter(([, v]) => v != null && v !== "")
      .map(([k, v]) => `${k}:${v}`)
      .join(";");
   return `<${tag} style="${style}">${content}</${tag}>`;
};

// Modified text() function with multi-language support
export function text(
   textObj?: BrickText,
   caption?: string,
   ...defaultSpecs: string[]
): string {
   // Determine the actual string to render with language support
   const { value } = parseMultiLanguageDefaults(
      [caption, ...defaultSpecs].filter(Boolean)
   );

   // Use the value if found, otherwise fall back to textObj's value
   const displayValue = value || textObj?.value || "";

   // Collect CSS props (fall back to textObj's own values)
   const props: Record<string, string | undefined> = {
      color: textObj?.color,
      "font-size": textObj?.fontSize,
      "font-family": textObj?.fontFamily,
      "font-weight": textObj?.fontWeight || (textObj?.bold ? "bold" : undefined),
      "background-color": textObj?.backgroundColor,
      "text-align": textObj?.align,
      margin: textObj?.margin,
   };

   return TagBuilder("span", displayValue, props);
}

// Modified button() function with multi-language support
export function button(
   btn?: BrickButton,
   url?: string,
   caption?: string,
   ...defaultSpecs: string[]
): string {
   // Determine the actual string to render with language support
   const { value } = parseMultiLanguageDefaults(
      [caption, ...defaultSpecs].filter(Boolean)
   );

   // Use the value if found, otherwise fall back to btn's value
   const displayValue = value || btn?.value || "";

   // Base inline style
   const baseProps: Record<string, string> = {
      "background-color": btn?.backgroundColor || "#993300",
      color: btn?.color || "#FFFFFF",
      "border-color": btn?.borderColor || "#FFFFFF",
      "border-width": btn?.url ? "2px" : "1px",
      "border-style": "solid",
      "border-radius": "0px",
      "font-family": btn?.fontFamily || "Assistant, verdana, geneva, sans-serif",
      "font-size": btn?.fontSize || "14px",
      "font-weight": "normal",
      "letter-spacing": "1px",
      "line-height": "30px",
      padding: "12px 30px",
      display: "inline-block",
      "text-decoration": "none",
      "text-align": btn?.align || "center",
   };

   const wrapperAttrs = `align="${btn?.align || "center"}" style="padding:${
      btn?.margin || "20px 0"
   }"`;
   const innerTableAttrs = `style="text-align:${btn?.align || "center"}"`;

   if (url) {
      // <a> button
      const style = Object.entries(baseProps)
         .map(([k, v]) => `${k}:${v}`)
         .join(";");
      return `
      <table border="0" cellpadding="0" cellspacing="0" class="module" data-role="module-button" data-type="button" role="module" width="100%">
        <tbody>
          <tr>
            <td ${wrapperAttrs} class="outer-td">
              <table border="0" cellpadding="0" cellspacing="0" class="wrapper-mobile" ${innerTableAttrs}>
                <tbody>
                  <tr>
                    <td align="${btn?.align || "center"}" class="inner-td" style="border-radius:6px; background-color:inherit; font-size:16px;">
                      <a href="${url}" target="_blank" style="${style}">
                        ${displayValue}
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    `.trim();
   } else {
      // <span> fallback
      const style = Object.entries(baseProps)
         .map(([k, v]) => `${k}:${v}`)
         .join(";");
      return `
      <table border="0" cellpadding="0" cellspacing="0" class="module" data-role="module-button" data-type="button" role="module" width="100%">
        <tbody>
          <tr>
            <td ${wrapperAttrs} class="outer-td">
              <table border="0" cellpadding="0" cellspacing="0" class="wrapper-mobile" ${innerTableAttrs}>
                <tbody>
                  <tr>
                    <td align="${btn?.align || "center"}" class="inner-td" style="border-radius:6px; background-color:inherit; font-size:16px;">
                      <span style="${style}">
                        ${displayValue}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    `.trim();
   }
}

// Modified box() function with multi-language support
function box(
   boxObj: BrickBox,
   caption?: string,
   ...defaultSpecs: string[]
): string {
   // Determine the actual string to render with language support
   const { value } = parseMultiLanguageDefaults(
      [caption, ...defaultSpecs].filter(Boolean)
   );

   // Use the value if found, otherwise fall back to boxObj's value
   const displayValue = value || boxObj?.value || "";

   // Hand off to the full BoxWidget
   return BoxWidget({ ...boxObj, value: displayValue });
}

export const ShippingBrickHtml = (
   brick: BrickShipping,
   mail: BrickMailDefinition
): string => {
   const {
      shippingImageQR,
      shippingLinkQR,
      labelCode,
      shippingLink,
      carrierName,
   } = {
      shippingImageQR: "https://storageaccountourimbea2.blob.core.windows.net/shippinglabels/67066da390e8b69c927e1f8d/67a39de5aab3463ada9e5ba1_qr.png",
      shippingLinkQR: "QR Link",
      labelCode: "Label Code",
      shippingLink: "Shipping Link",
      carrierName: "Carrier",
   };

   const align   = brick.align  || 'center';
   const margin  = brick.margin || '0px 0px 0px 0px';
   const layoutButtonWithAlign = { ...brick.layoutButton, align } as any;

   // build the inner block once, based on which variables exist
   let innerHtml: string = ''
   if (shippingImageQR) {
      innerHtml += `
      <div style="text-align:${align};">
        ${text(brick.layoutText as any, brick.qrImageTitle,
         'default=Here is your QR code',
         'default.da=Her er din QR-kode',
         'default.de=Hier ist Ihr QR-Code'
      )}
      </div>
      <div style="text-align:${align};">
        <img
          src="${shippingImageQR}"
          style="padding: 0 50px 0 50px;border:0;height:auto;line-height:100%;max-width:100%;outline:none;text-decoration:none;display:block;font-size:13px;width:100%"
          width="400"
          data-bit="iit"
          tabindex="0"
        />
      </div>
    `;
   }
   if (shippingLinkQR) {
      innerHtml += `
      <div style="text-align:${align};">
        ${text(
         brick.layoutText as any,
         brick.qrDownloadTitle,
         `default=Here is a link to your ${carrierName} QR-code, that you can show when dropping off the package:`,
         `default.da=Her er et link til din ${carrierName} QR-kode, som du kan vise, når du afleverer pakken:`,
         `default.de=Hier ist ein Link zu Ihrem ${carrierName} QR-Code, den Sie beim Abgeben des Pakets vorzeigen können:`
      )}
      </div>
      <div style="text-align:${align};">
        ${button(layoutButtonWithAlign, shippingLinkQR, brick.qrDownloadButtonTitle,
         'default=Download QR',
         'default.da=Download QR',
         'default.de=QR herunterladen'
      )}
      </div>
    `;
   }
   if (labelCode && labelCode !== 'none') {
      innerHtml += `
      <div style="text-align:${align};">
        ${text(
         brick.layoutText as any,
         brick.labelCodeTitle,
         `default=Here is your ${carrierName} package code. Please write it on the package:`,
         `default.da=Her er din ${carrierName} pakkekode. Skriv den venligst på pakken:`,
         `default.de=Hier ist Ihr ${carrierName} Paketcode. Bitte schreiben Sie ihn auf das Paket:`
      )}
      </div>
      <div style="text-align:${align};">
        ${box(layoutButtonWithAlign, labelCode)}
      </div>
    `;
   }
   if (shippingLink) {
      innerHtml += `
      <div style="text-align:${align};">
        ${text(
         brick.layoutText as any,
         brick.shippingTitle,
         `default=At the link below, you'll find your ${carrierName} label. Please print it and attach it to the package:`,
         `default.da=På linket nedenfor finder du din ${carrierName} label. Print den venligst ud og sæt den på pakken:`,
         `default.de=Unter dem folgenden Link finden Sie Ihr ${carrierName} Etikett. Bitte drucken Sie es aus und befestigen Sie es am Paket:`
      )}
      </div>
      <div style="text-align:${align};">
        ${button(layoutButtonWithAlign, shippingLink, brick.shippingButtonTitle,
         'default=Download label',
         'default.da=Download label',
         'default.de=Etikett herunterladen'
      )}
      </div>
    `;
   }

   // finally return the full table wrapper
   return `
    <div style="padding:${margin}">
      <table
        class="module"
        role="module"
        data-type="text"
        border="0"
        cellpadding="0"
        cellspacing="0"
        width="100%"
      >
        <tbody>
          <tr>
            <td style="line-height:110%; text-align:${align};">
              ${innerHtml.trim()}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  `;
};


export const ButtonBrickHtml = (brick: BrickButton, mail: BrickMailDefinition) => { 
   // Destructure and apply defaults
   const {
      fullwidth = false,
      align = 'center',
      margin = '20px 0px',
      backgroundColor = '#993300',
      borderColor = '#FFFFFF',
      borderWidth = '2px',
      color = '#FFFFFF',
      fontFamily = mail.body?.font || 'Assistant, verdana, geneva, sans-serif',
      fontSize = '14px',
      padding = brick.padding || '12px 30px',
      value = '',
      url,
      borderRadius = brick.borderRadius || '0px'
   } = brick;

   const lineHeight = "120%";

   // Style for the TD element when handling borders
   const tdStyle = `border:${borderWidth} solid ${borderColor};border-radius:${borderRadius};cursor:${url ? 'pointer' : 'auto'};mso-padding-alt:8px 24px 7px;background:${backgroundColor};`;

   // Style for the A/SPAN element
   const linkStyle = `display:inline-block;background-color:${backgroundColor};color:${color};font-family:${fontFamily};font-size:${fontSize} !important;line-height:${lineHeight};text-align:${align};text-decoration:none;mso-hide:all;padding:${padding};`;

   // When fullwidth is true, TD handles the styling - A/SPAN doesn't need border, radius, etc.
   const fullwidthLinkStyle = `mso-padding-alt:0px;display:${fullwidth ? 'block' : 'inline-block'};color:${color};font-family:${fontFamily};font-size:${fontSize} !important;line-height:${lineHeight};text-align:${align};text-decoration:none;mso-hide:all;padding:${padding};`;

   return ` 
    <!-- Button --> 
    <table border="0" cellpadding="0" cellspacing="0" width="100%" style="table-layout:fixed"> 
      <tbody> 
        <tr> 
          <td ${fullwidth ? 'width="100%"' : ''} align="${align}" style="padding:${margin}; ${fullwidth ? 'width:100%;' : ''}"> 
            <table border="0" cellpadding="0" cellspacing="0" ${fullwidth ? 'width="100%"' : ''} style="line-height:100%;border-collapse:separate; table-layout:fixed; text-align:${align}; ${fullwidth ? 'width:100%;' : ''}"> 
              <tbody> 
                <tr> 
                  <td align="${align}" bgcolor="${backgroundColor}" role="presentation" style="${tdStyle}" valign="middle"> 
                      ${url ? `<a href="${url}" target="_blank" style="${fullwidth ? fullwidthLinkStyle : linkStyle}">${value}</a>`
      : `<span style="${fullwidth ? fullwidthLinkStyle : linkStyle}">${value}</span>`
   } 
                  </td> 
                </tr> 
              </tbody> 
            </table> 
          </td> 
        </tr> 
      </tbody> 
    </table> 
  `;
};


export const ProductsBrickHtml = (brick: BrickProducts, mail: BrickMailDefinition, instance: BrickMailInstance) => `<!-- Products -->
<table border="0" cellpadding="0" cellspacing="0" class="module"
       data-role="module-button" data-type="button"
       role="module" style="table-layout:fixed" width="100%"
       data-muid="hthYAt191yTdg6FPWYKodF">
    <tbody>
    <tr>
        <td width="20%" align="center" bgcolor="#e0e0e0" class="outer-td" style="padding:0px 10px 0px 10px;">

    ${(instance.products?.data||[]).length > 0 ? `<span style="font-weight:bold;">${brick.title}</span><br/><br/><br/>` : ``}

${instance.products?.data.map(product => `<table border="0" cellpadding="0" cellspacing="0" class="module"
      data-role="module-button" data-type="button"
      role="module" style="table-layout:fixed" width="100%"
      data-muid="hthYAt191yTdg6FPWYKodF">
      <tbody>
         <tr>
            <td width="20%" align="center" bgcolor="#e0e0e0" class="outer-td" style="padding:0px 0px 40px 0px;">
      <a href="${instance.variables.loginLink}" target="_blank"><img width="80" border="0" style="width:80px; color:#000000; text-decoration:none; font-family:${mail.body?.font || 'Assistant, verdana, geneva, sans-serif'}; font-size:14px; max-width:100% !important; height:auto !important;"
      alt="" data-proportionally-constrained="true" data-responsive="true" src="${product.image}"></a>
         </td>
         <td width="60%" align="left" class="outer-td" style="padding:0px 0px 40px 10px;">
      <a href="${instance.variables.loginLink}" target="_blank" style="color:#000000; text-decoration:none; font-family:${mail.body?.font || 'Assistant, verdana, geneva, sans-serif'}; font-size:13px; max-width:100% !important; height:auto !important;">
         ${product.title}
      </a>
      <table border="0" cellpadding="0" cellspacing="0" class="module"
      data-role="module-button" data-type="button"
      role="module" style="table-layout:fixed" width="100%"
      data-muid="hthYAt191yTdg6FPWYKodF">
      <tr><td width="40%" align="left" class="outer-td" style="padding:0px 0px 0px 0px; background-color:#ffffff;">
      <span style="color:#808080; text-decoration:none; font-family:${mail.body?.font || 'Assistant, verdana, geneva, sans-serif'}; font-size:10px; max-width:100% !important; height:auto !important;line-height: 12px;">
         ${instance.products?.boughtPrefix} ${product.orderDate}
      </span><br/>
      </td><td width="60%" align="right" class="outer-td" style="padding:0px 0px 0px 0px;">
      <table cellpadding="0" style="padding: 10px; background-color: #f0f0f0; color:#000000; text-decoration:none; font-family:${mail.body?.font || 'Assistant, verdana, geneva, sans-serif'}; font-size:12px; max-width:100% !important; height:auto !important;"><tr><td>
         <span style="background-color: #f0f0f0; color:#000000; text-decoration:none; font-family:${mail.body?.font || 'Assistant, verdana, geneva, sans-serif'}; font-size:11px; max-width:100% !important; height:auto !important;">
         ${instance.products?.voucherPrefix} ${product.voucher}
      </span>
      </td></tr></table>
      </td>
      </tr>
      </table>
      </td>
      </tr>
      </tbody>
      </table>
`)}
    </td>
    </tbody>
</table>
`


export const BoxBrickHtml = (brick: BrickBox, mail: BrickMailDefinition) => `
  <!-- Box -->
  <table border="0" cellpadding="0" cellspacing="0" class="module" data-role="module-button" data-type="button" role="module" style="table-layout:fixed" width="100%">
    <tbody>
      <tr>
        <td align="${brick.align || 'center'}" class="outer-td"
          style="padding:${brick.margin || '20px 0px 20px 0px'}; background-color:inherit">
          <table border="0" cellpadding="0" cellspacing="0"
            class="button-css__deep-table___2OZyb wrapper-mobile"
            style="text-align:${brick.align || 'center'}">
            <tbody>
              <tr>
                <td align="${brick.align || 'center'}"
                  bgcolor="${brick.backgroundColor || '#993300'}"
                  class="inner-td"
                  style="border-radius:6px; font-size:16px; text-align:${brick.align || 'center'}; background-color:inherit;">
                  <span style="
                    background-color:${brick.backgroundColor || '#993300'};
                    border:2px solid ${brick.backgroundColor || '#993300'};
                    border-color:${brick.borderColor || '#993300'};
                    border-radius:0px;
                    border-width:1px;
                    color:${brick.color || '#FFFFFF'};
                    display:inline-block;
                    font-family: ${brick.fontFamily || mail.body?.font || 'Assistant, verdana, geneva, sans-serif'};
                    font-size:${brick.fontSize || '16px'} !important;
                    font-weight:normal;
                    letter-spacing:1px;
                    line-height:30px;
                    padding:${brick.padding || '12px 30px 12px 30px'};
                    text-align:${brick.align || 'center'};
                    text-decoration:none;
                    border-style:solid;
                    ${brick.width ? `width:${brick.width};` : ''}">
                    ${brick.value || ''}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </td>
      </tr>
    </tbody>
  </table>`


export const StartHtml = (mail: BrickMailDefinition) => `<body style="background-color:${mail.body?.backgroundColor || '#ffffff'}" bgcolor="${mail.body?.backgroundColor || '#ffffff'}">
<center class="wrapper" data-link-color="#1188E6"
data-body-style="font-size:14px; font-family:${mail.body?.font || 'Assistant, verdana, geneva, sans-serif'}; color:#000000; background-color:${mail.body?.backgroundColor || '#ffffff'};">
<div class="webkit">
<table cellpadding="0" cellspacing="0" border="0" width="100%" class="wrapper" bgcolor="${mail.body?.backgroundColor || '#ffffff'}">
<tr>
<td valign="top" bgcolor="${mail.body?.backgroundColor || '#ffffff'}" width="100%">
<table width="100%" role="content-container" class="outer" align="center" cellpadding="0"
cellspacing="0" border="0">
<tr>
<td width="100%">
<table width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr>
        <td>
            <!--[if mso]>
            <center>
                <table>
                    <tr>
                        <td width="600">
            <![endif]-->
            <table width="100%" cellpadding="0" cellspacing="0" border="0"
                   style="width:100%; max-width:600px;" align="center">
                <tr>
                    <td role="modules-container"
                        style="padding:0px 0px 0px 0px; color:#000000; text-align:left;"
                        bgcolor="${mail.title?.backgroundColor || '#F4F6F5'}" width="100%" align="left">
                        <table class="module preheader preheader-hide" role="module"
                               data-type="preheader" border="0" cellpadding="0"
                               cellspacing="0" width="100%"
                               style="display: none !important; mso-hide: all; visibility: hidden; opacity: 0; color: transparent; height: 0; width: 0;">
                            <tr>
                                <td role="module-content">
                                    <div style="display: none; max-height: 0px; overflow: hidden;">
                                    ${mail.title?.text}
                                    </div>
                                </td>
                            </tr>
                        </table>
                        ${mail.title ? `
                        <table class="module" role="module" data-type="text" border="0"
                               cellpadding="0" cellspacing="0" width="100%"
                               style="table-layout: fixed;"
                               data-muid="580f22fc-39aa-468e-9753-3a0d0a23fd57"
                               data-mc-module-version="2019-10-22">
                            <tbody>
                            <tr>
                                <td style="padding:38px 0px 0px 0px; line-height:22px; text-align:inherit;"
                                    height="100%" valign="top"
                                    role="module-content">
                                    <div>
                                        <div style="font-family: inherit; font-size: 16px; text-align: center; letter-spacing: 0.35rem">
                                            ${mail.title?.url ? `
                                            <a target="_blank" href="${mail.title?.url}" style="color: #000000; text-decoration: none">                               
                                            ` : ``}
                                            ${mail.title?.logo ? `
                                                <img border="0" style="color:#000000; text-decoration:none; font-family:${mail.body?.font || 'Assistant, verdana, geneva, sans-serif'}; font-size:18px; max-width:100% !important; height:auto !important;"
                                                                     alt="${mail.title?.shop}"
                                                                     data-proportionally-constrained="true"
                                                                     data-responsive="true" src="${mail.title?.logo}" title="${mail.title?.text}">
                                            ` : `
                                                <span style="color: #000000; text-decoration: none">
                                                    ${mail.title?.text}
                                                </span>
                                            `}
                                            ${mail.title?.url ? `
                                            </a>` : ''}
                                        </div>
                                        <div></div>
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                        ` : ``}
                        <table border="0" cellpadding="0" cellspacing="0" align="center"
                               width="100%" role="module" data-type="columns"
                               style="padding:0px 0px 0px 0px;" bgcolor="${mail.body?.frameColor || '#C0C0C0'}"
                               data-distribution="1">
                            <tbody>
                            <tr role="module-content">
                                <td height="100%" valign="top">
                                    <table width="600"
                                           style="width:600px; border-spacing:0; border-collapse:collapse; margin:0px 0px 5px 0px;"
                                           cellpadding="0" cellspacing="0" align="left"
                                           border="0" bgcolor=""
                                           class="column column-0">
                                        <tbody>
                                        <tr>
                                            <td style="padding:${mail.body?.frame ? `${mail.body.framePadding || '15px 15px 10px 15px'}` : `0`};margin:0px;border-spacing:0;">
                                                <table class="module" role="module"
                                                       data-type="text" border="0"
                                                       cellpadding="0" cellspacing="0"
                                                       width="100%"
                                                       style="table-layout: fixed;"
                                                       data-muid="3b03c927-16c0-4abb-b355-9678f2ee3cde"
                                                       data-mc-module-version="2019-10-22">
                                                    <tbody>
                                                    <tr>
                                                        <td style="padding:${mail.body?.padding || '35px 15px 25px 15px'}; line-height:22px; text-align:inherit; background-color: ${mail.body?.backgroundColor || '#ffffff'};"
                                                            height="100%" valign="top"
                                                            bgcolor="${mail.body?.textBackgroundColor || '#ffffff'}"
                                                            role="module-content">

                                                <div id="mail-root"/>

                                        ${mail.speakbox ? `${SpeakBox(mail)}` : ``}
                                </td>
                            </tr>
                            </tbody>
                        </table>

                    </td>
                </tr>
            </table>
            <!--[if mso]>
            </td>
            </tr>
            </table>
            </center>
            <![endif]-->
        </td>
    </tr>
</table>
</td>
</tr>
</table>
</div>
</center>
</body>`


export const SpeakBox = (mail: BrickMailDefinition) => `<table width="100%" border="0" cellpadding="0"
       cellspacing="0"
       align="center" bgcolor="${mail.speakbox?.backgroundColor || '#F0F0F0'}"
       style="width: 100%; background-color: ${mail.speakbox?.backgroundColor || '#F0F0F0'}">
    
    ${mail.speakbox?.box1image && mail.speakbox.box2image ? ` 
    <tr>
        <td style="padding-top: 20px;width:50%" width="50%">
            <table align="center" width="100%"
                   style="width:100%">
                <tr>
                    <td style="font-size:6px; line-height:16px; padding:0px 0px 0px 0px;"
                        valign="top" align="center">
                        <img 
                             border="0"
                             style="display:block; color: ${mail.speakbox?.color || '#000000'}; text-decoration:none; font-family:${mail.body?.font || 'Assistant, verdana, geneva, sans-serif'}; font-size:18px; width:40px;"
                             width="40" alt=""
                             data-proportionally-constrained="true"
                             data-responsive="true"
                             src="${mail.speakbox?.box1image}">
                    </td>
                </tr>
            </table>
        </td>
        <td style="padding-top: 20px;width:50%" width="50%">
            <table align="center" width="100%"
                   style="width:100%">
                <tr>
                    <td style="font-size:6px; line-height:16px; padding:0px 0px 0px 0px;"
                        valign="top" align="center">
                        <img 
                             border="0"
                             style="display:block; color: ${mail.speakbox?.color || '#000000'}; text-decoration:none; font-family:${mail.body?.font || 'Assistant, verdana, geneva, sans-serif'}; font-size:18px; width:40px;"
                             width="40" alt=""
                             data-proportionally-constrained="true"
                             data-responsive="true"
                             src="${mail.speakbox?.box2image}">
                    </td>
                </tr>
            </table>
        </td>
    </tr>
    ` : ``}
    <tr>
        ${mail.speakbox?.box1 ? ` 
        <td valign="top" style="padding-bottom: 20px">
            <div style="font-family:${mail.body?.font|| 'Assistant, verdana, geneva, sans-serif' }; text-align: center">
                <span style="color: ${mail.speakbox?.color ||'#000000' }; ${mail.speakbox?.fontFamily ? `font-family: ${mail.speakbox.fontFamily}`:``}; font-size: ${mail.speakbox?.fontSize || '11px' }">
                ${mail.speakbox?.box1}
                </span>
            </div>
            <div style="font-family:${mail.body?.font|| 'Assistant, verdana, geneva, sans-serif' }; text-align: center">
                <span style="color: ${mail.speakbox?.colorDesc ||'#000000' }; ${mail.speakbox?.fontFamily ? `font-family: ${mail.speakbox.fontFamily}`:``}; font-size: ${mail.speakbox?.fontSize || '11px' }">
                ${mail.speakbox?.box1desc}
                </span>
            </div>
        </td>
        ` : ``}
        ${mail.speakbox?.box2 ? `
        <td valign="top" style="padding-bottom: 20px">
            <div style="font-family:${mail.body?.font|| 'Assistant, verdana, geneva, sans-serif' }; text-align: center">
                <span style="color: ${mail.speakbox?.color ||'#000000' }; ${mail.speakbox?.fontFamily ? `font-family: ${mail.speakbox.fontFamily}`:``}; font-size: ${mail.speakbox?.fontSize || '11px' }">
                  ${mail.speakbox?.box1}
                </span>
            </div>
            
            <div style="font-family:${mail.body?.font|| 'Assistant, verdana, geneva, sans-serif' }; text-align: center">
                <span style="color: ${mail.speakbox?.colorDesc ||'#000000' }; ${mail.speakbox?.fontFamily ? `font-family: ${mail.speakbox.fontFamily}`:``}; font-size: ${mail.speakbox?.fontSize || '11px' }">
                  ${mail.speakbox?.box1desc}
                </span>
            </div>
        </td>
        ` : ``}
    </tr>
</table>

</td>
</tr>
</tbody>
</table>
`


export const BoxWidget = (box: BrickBox) => `
  <!-- Box -->
  <table border="0" cellpadding="0" cellspacing="0" class="module" data-role="module-button" data-type="button" role="module" style="table-layout:fixed" width="100%">
    <tbody>
      <tr>
        <td align="${box.align || 'center'}" class="outer-td"
          style="padding:${box.margin || '20px 0px 20px 0px'}; background-color:inherit">
          <table border="0" cellpadding="0" cellspacing="0"
            class="button-css__deep-table___2OZyb wrapper-mobile"
            style="text-align:${box.align || 'center'}">
            <tbody>
              <tr>
                <td align="${box.align || 'center'}"
                  bgcolor="${box.backgroundColor || '#993300'}"
                  class="inner-td"
                  style="border-radius:6px; font-size:16px; text-align:${box.align || 'center'}; background-color:inherit;">
                  <span style="
                    background-color:${box.backgroundColor || '#993300'};
                    border:2px solid ${box.backgroundColor || '#993300'};
                    border-color:${box.borderColor || '#993300'};
                    border-radius:0px;
                    border-width:1px;
                    color:${box.color || '#FFFFFF'};
                    display:inline-block;
                    font-family:${box.fontFamily || 'Assistant, verdana, geneva, sans-serif'};
                    font-size:${box.fontSize || '16px'} !important;
                    font-weight:normal;
                    letter-spacing:1px;
                    line-height:30px;
                    padding:12px 30px 12px 30px;
                    text-align:${box.align || 'center'};
                    text-decoration:none;
                    border-style:solid;">
                    ${box.value || ''}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </td>
      </tr>
    </tbody>
  </table>
`.replace('\n','');

export const ButtonWidget = (button: BrickButton) => `
  <table border="0" cellpadding="0" cellspacing="0" class="module" data-role="module-button" data-type="button" role="module" style="table-layout:fixed" width="100%">
    <tbody>
      <tr>
        <td align="${button.align || 'center'}" class="outer-td" style="padding:${button.margin || '20px 0px 20px 0px'};">
          <table border="0" cellpadding="0" cellspacing="0" class="wrapper-mobile" style="text-align:${button.align || 'center'}">
            <tbody>
              <tr>
                <td align="${button.align || 'center'}" bgcolor="${button.backgroundColor || '#993300'}" class="inner-td" style="border-radius:6px; font-size:16px; text-align:${button.align || 'center'}; background-color:inherit;">
                  ${button.url
   ? `<a style="
                        background-color:${button.backgroundColor || '#993300'};
                        border:2px solid ${button.borderColor || '#FFFFFF'};
                        border-color:${button.borderColor || '#FFFFFF'};
                        border-radius:0px;
                        border-width:2px;
                        color:${button.color || '#FFFFFF'};
                        display:inline-block;
                        ${button.fontFamily ? `font-family:${button.fontFamily};` : ''}
                        font-size:${button.fontSize || '14px'} !important;
                        font-weight:normal;
                        letter-spacing:1px;
                        line-height:30px;
                        padding:12px 30px 12px 30px;
                        text-align:${button.align || 'center'};
                        text-decoration:none;
                        border-style:solid;"
                        href="${button.url}"
                        target="_blank">
                    `
   : `<span style="
                        background-color:${button.backgroundColor || '#993300'};
                        border:2px solid ${button.backgroundColor || '#993300'};
                        border-color:${button.backgroundColor || '#993300'};
                        border-radius:0px;
                        border-width:1px;
                        color:${button.color || '#FFFFFF'};
                        display:inline-block;
                        font-family:${button.fontFamily || 'Assistant, verdana, geneva, sans-serif'};
                        font-size:${button.fontSize || '14px'} !important;
                        font-weight:normal;
                        letter-spacing:1px;
                        line-height:30px;
                        padding:12px 30px 12px 30px;
                        text-align:${button.align || 'center'};
                        text-decoration:none;
                        border-style:solid;">
                    `}
                    ${button.value || ''}
                  ${button.url ? `</a>` : `</span>`}
                </td>
              </tr>
            </tbody>
          </table>
        </td>
      </tr>
    </tbody>
  </table>
`.replace('\n','');


export const StatusBrickHtml = (brick: BrickStatus, mail: BrickMailDefinition) => {
   // Create a placeholder for trade-in items that would be populated in the real email
   const sampleTradeInItems = [
      {
         imageUrl: 'https://storageaccountourimbea2.blob.core.windows.net/brandstatic/App_Icon3.png',
         title: 'Sample Product 1',
         status: 'OKAY',
         value: '50 EUR'
      },
      {
         imageUrl: 'https://storageaccountourimbea2.blob.core.windows.net/brandstatic/App_Icon3.png',
         title: 'Sample Product 2',
         status: 'REJECTDEFAULT',
         value: '75 EUR'
      }
   ];

   // Get alignment and margin from brick properties
   const align = brick.align || 'center';
   const margin = brick.margin || '0px 5px 0px 5px';
   const fontSize = brick.fontSize || '14px';
   const fontFamily = brick.fontFamily ? `font-family: ${brick.fontFamily};` : '';
   const backgroundColor = brick.backgroundColor || 'inherit';
   const color = brick.color || '#000000';
   const fontWeight = brick.bold ? 'font-weight: bold;' : '';

   // Recreate the server-side TagBuilder implementation
   const tagBuilder = (tag: string, content: string, attributes: { [key: string]: string | undefined }) => {
      const attributeString = Object.keys(attributes)
         .filter(key => !!attributes[key])
         .map(key => `${key}:${attributes[key]}`).join(";");
      return `<${tag} style="${attributeString}">${content}</${tag}>`;
   };

   // Function that mimics the blocktext helper from server-side
   const getBlockText = (field: string, caption?: string) => {
      if (!brick.blocks || !brick.blocks[field]) {
         return caption || '';
      }

      const layout = brick.blocks[field];
      // Use caption if provided, otherwise use the block's text
      const text = caption && typeof caption === "string" ? caption : layout.text;

      return tagBuilder('span', text, {
         "color": layout.color || brick.color,
         "font-size": layout.fontSize || brick.fontSize,
         "font-family": layout.fontFamily || brick.fontFamily,
         "font-weight": layout.fontWeight || brick.fontWeight,
         "background-color": layout.backgroundColor || brick.backgroundColor,
         "align": layout.align || brick.align
      });
   };

   // Function to render item status
   const renderItemStatus = (item: { status: string }) => {
      const statusKey = item.status;
      return getBlockText(statusKey);
   };

   let tableRows = '';
   sampleTradeInItems.forEach(item => {
      tableRows += `
      <tr class="order-list__item" style="width: 100%;">
         <td style="text-align: left; padding-bottom: 10px; line-height:110%; ${fontFamily} font-size: ${fontSize} !important; ${fontWeight} width: 55px;">
            <a style="text-underline: none" rel="noopener noreferrer" target="_blank" href="${item.imageUrl}">
               <img width="40" border="0"
                  style="width: 40px; margin-right: 15px; border-radius: 8px; border: 1px solid #e5e5e5;"
                  alt=""
                  data-proportionally-constrained="true"
                  data-responsive="true" src="${item.imageUrl}"
               >
            </a>
         </td>
         <td class="order-list__product-description-cell"
            style="padding-left: 10px; padding-bottom: 10px; text-align: left; line-height:110%; background-color: ${backgroundColor}; color: ${color}; ${fontFamily} font-size: ${fontSize} !important; ${fontWeight} width: 445px;">
            <div>${getBlockText("ITEM", item.title)}</div>
            <div>${renderItemStatus(item)}</div>
         </td>
         <td class="order-list__price-cell"
            style="padding-bottom: 10px; text-align: right; line-height:110%; background-color: ${backgroundColor}; color: ${color}; ${fontFamily} font-size: ${fontSize} !important; ${fontWeight} white-space: nowrap; width: 100px">
            ${item.value}
         </td>
      </tr>`;
   });

   // Construct the HTML based on the template from paste.txt
   return `
   <div style="padding:${margin}">
      ${brick.blocks?.TITLE ? `
      <table class="module" role="module" data-type="text" border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom: 10px">
         <tbody><tr><td style="line-height:130%; text-align:${align};">
            <div style="line-height:130%; font-family: ${mail.body?.font || 'Assistant, verdana, geneva, sans-serif'}; text-align: ${align};">
               ${getBlockText("TITLE", "Trade-in Items")}
            </div>
         </td></tr>
         </tbody>
      </table>
      ` : ''}
      
      ${brick.blocks?.DESCRIPTION ? `
      <table class="module" role="module" data-type="text" border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom: 10px">
         <tbody><tr><td style="line-height:130%; text-align:${align};">
            <div style="line-height:130%; font-family: ${mail.body?.font || 'Assistant, verdana, geneva, sans-serif'}; text-align: ${align};">
               ${getBlockText("DESCRIPTION", "Here are the items you have traded in and their status")}
            </div>
         </td></tr>
         </tbody>
      </table>
      ` : ''}

      <table cellspacing="0" cellpadding="0" class="row" style="width: 100%; border-spacing: 0; border-collapse: collapse;">
         <tbody>
            <tr><td colspan="3" style="line-height: 70%; border-top: 1px solid black;">&nbsp;</td></tr>
            ${tableRows}
            <tr><td colspan="3" style="line-height: 30%">&nbsp;</td></tr>
            <tr><td colspan="3" style="line-height: 70%; border-top: 1px solid black;">&nbsp;</td></tr>

            <tr class="order-list__item" style="width: 100%;">
               <td align="left" colspan="2" style="text-align: left; width: 80%; line-height:130%; background-color: ${backgroundColor}; color: ${color}; ${fontFamily} font-size: ${fontSize} !important; ${fontWeight}">
                  ${getBlockText("SUMTOTAL", "Total value:")}
               </td>
               <td class="order-list__price-cell"
                  style="text-align: right; line-height:130%; background-color: ${backgroundColor}; color: ${color}; ${fontFamily} font-size: ${fontSize} !important; ${fontWeight} white-space: nowrap; width: 20%;">
                  ${getBlockText("VOUCHER", "100 EUR")}
               </td>
            </tr>
            <tr><td colspan="3" style="line-height: 40%">&nbsp;</td></tr>
            <tr><td colspan="3" style="line-height: 70%; border-top: 1px double black;">&nbsp;</td></tr>
         </tbody>
      </table>
   </div>
   `;
};